/**
 * `SnapshotClient` — read-only loader for the published `portfolio.json.gz`.
 *
 * The client fetches the snapshot with an `AbortSignal` timeout, pipes the
 * response body through `DecompressionStream("gzip")`, JSON-parses the
 * result, validates it as a `Portfolio`, and caches the outcome. The
 * cache is consumed by both `hasDeck` and `fetchDeckUrl`.
 *
 * Read-only guarantee (Requirement 17.5): the `SnapshotClient` interface
 * intentionally exposes NO methods that could mutate the portfolio, the
 * cache, or any persistent state. That guarantee is enforced at the type
 * level here — the shell has no lever to reach for.
 *
 * On any load failure the returned promise rejects with a
 * `SnapshotLoadError` carrying a discriminated `code`. The portal shell
 * (task 7.7) is responsible for catching the rejection, exposing an
 * empty `Case[]` to the rest of the UI, and surfacing the error via
 * the reactive store — per Requirement 17.4.
 */
import type { Case, Portfolio } from "@aim/types";

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

/**
 * Read-only view of a published snapshot.
 *
 * Every method returns primitive/immutable results. There is no writer,
 * no clearer, no reloader — the shell reconstructs a fresh client if it
 * ever wants to reload.
 */
export interface SnapshotClient {
  /**
   * Return the cases from the snapshot. Fetches, decompresses, parses,
   * and validates on the first call; subsequent calls resolve from the
   * cache without re-fetching.
   */
  listCases(): Promise<Case[]>;

  /**
   * URL to the deck for a case within the published snapshot, if any.
   * Returns `""` when the case is unknown or when `listCases()` has not
   * yet resolved.
   *
   * @see hasDeck for the boolean predicate.
   */
  fetchDeckUrl(id: string): string;

  /**
   * Whether the case is present in the loaded snapshot.
   *
   * NOTE (task 9.2): The publish task will populate a `partialDecks`
   * marker on `PublishResult` identifying cases whose `.pptx` file was
   * absent at publish time. Once that marker is propagated into the
   * snapshot payload, this predicate should consult it. Until then the
   * predicate approximates: any case present in the loaded snapshot is
   * assumed to have a deck. See design.md § Publish flow.
   */
  hasDeck(id: string): boolean;
}

export interface SnapshotClientOptions {
  /** URL of the compressed portfolio snapshot, e.g. `"portfolio.json.gz"`. */
  snapshotUrl: string;
  /** Directory prefix (no trailing slash) for deck files; defaults to `"decks"`. */
  decksBase?: string;
  /** Injectable `fetch` for tests; defaults to `globalThis.fetch`. */
  fetchImpl?: typeof fetch;
  /**
   * Injectable `DecompressionStream` constructor for tests. Callers may
   * pass an identity `TransformStream` to bypass real gzip in unit tests.
   * Defaults to `globalThis.DecompressionStream`.
   */
  decompressionStream?: typeof DecompressionStream;
  /** Fetch timeout in milliseconds; defaults to 30_000. */
  timeoutMs?: number;
}

/** Discriminated error codes for snapshot load failures. */
export type SnapshotLoadErrorCode =
  | "FETCH_FAILED"
  | "HTTP_ERROR"
  | "TIMEOUT"
  | "GZIP_FAILED"
  | "PARSE_FAILED"
  | "INVALID_PAYLOAD";

/**
 * Rejected-promise carrier for every failure that stops the snapshot
 * from being usable. Callers narrow on `code` for user-facing messaging.
 */
export class SnapshotLoadError extends Error {
  readonly code: SnapshotLoadErrorCode;
  readonly status?: number;

  constructor(
    code: SnapshotLoadErrorCode,
    message: string,
    opts?: { status?: number; cause?: unknown },
  ) {
    super(message);
    this.name = "SnapshotLoadError";
    this.code = code;
    if (opts?.status !== undefined) {
      this.status = opts.status;
    }
    if (opts?.cause !== undefined) {
      // `cause` is standard on `ErrorOptions` from ES2022; assign
      // through a widened type to keep the ctor signature compatible
      // with the design's explicit `opts` shape.
      (this as { cause?: unknown }).cause = opts.cause;
    }
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_DECKS_BASE = "decks";

/**
 * Structural check that a parsed JSON value satisfies the `Portfolio`
 * discriminant used at runtime. We deliberately keep this minimal —
 * anchor-level `Case` shape is trusted, since the payload was produced
 * by the same codebase at publish time. Downstream views tolerate
 * missing/nullable fields per Requirement 6.3.
 */
function isPortfolio(value: unknown): value is Portfolio {
  if (value === null || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return v.schemaVersion === 1 && Array.isArray(v.cases);
}

/** Normalize `decksBase` by stripping a trailing slash if present. */
function normalizeDecksBase(decksBase: string): string {
  return decksBase.endsWith("/") ? decksBase.slice(0, -1) : decksBase;
}

function describeError(cause: unknown): string {
  if (cause instanceof Error) return cause.message;
  return String(cause);
}

/**
 * Build a fresh, read-only `SnapshotClient` bound to a snapshot URL.
 *
 * The returned client is single-use in the sense that its cache is
 * populated exactly once per instance. Callers wanting to reload rebuild
 * the client.
 */
export function createSnapshotClient(
  opts: SnapshotClientOptions,
): SnapshotClient {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  const decompressionCtor =
    opts.decompressionStream ??
    (globalThis as { DecompressionStream?: typeof DecompressionStream })
      .DecompressionStream;
  const decksBase = normalizeDecksBase(opts.decksBase ?? DEFAULT_DECKS_BASE);
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  /** Cached, immutable `Portfolio` after a successful load. */
  let cache: Portfolio | null = null;
  /** In-flight load promise; used to dedupe concurrent `listCases` calls. */
  let inFlight: Promise<Case[]> | null = null;

  async function loadPortfolio(): Promise<Portfolio> {
    if (typeof fetchImpl !== "function") {
      throw new SnapshotLoadError(
        "FETCH_FAILED",
        "No fetch implementation available in this environment.",
      );
    }
    if (typeof decompressionCtor !== "function") {
      throw new SnapshotLoadError(
        "GZIP_FAILED",
        "No DecompressionStream implementation available in this environment.",
      );
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    let response: Response;
    try {
      response = await fetchImpl(opts.snapshotUrl, {
        signal: controller.signal,
      });
    } catch (cause) {
      // If we tripped the timeout, translate the abort to TIMEOUT so
      // callers can differentiate slow networks from truly failed ones.
      if (controller.signal.aborted) {
        throw new SnapshotLoadError(
          "TIMEOUT",
          `Snapshot fetch exceeded ${String(timeoutMs)}ms: ${opts.snapshotUrl}`,
          { cause },
        );
      }
      throw new SnapshotLoadError(
        "FETCH_FAILED",
        `Failed to fetch ${opts.snapshotUrl}: ${describeError(cause)}`,
        { cause },
      );
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      throw new SnapshotLoadError(
        "HTTP_ERROR",
        `HTTP ${String(response.status)} fetching ${opts.snapshotUrl}`,
        { status: response.status },
      );
    }

    if (response.body === null) {
      throw new SnapshotLoadError(
        "FETCH_FAILED",
        `Response for ${opts.snapshotUrl} had no body.`,
      );
    }

    let text: string;
    try {
      const stream = response.body.pipeThrough(new decompressionCtor("gzip"));
      text = await new Response(stream).text();
    } catch (cause) {
      throw new SnapshotLoadError(
        "GZIP_FAILED",
        `Failed to gunzip ${opts.snapshotUrl}: ${describeError(cause)}`,
        { cause },
      );
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (cause) {
      throw new SnapshotLoadError(
        "PARSE_FAILED",
        `Failed to JSON-parse ${opts.snapshotUrl}: ${describeError(cause)}`,
        { cause },
      );
    }

    if (!isPortfolio(parsed)) {
      throw new SnapshotLoadError(
        "INVALID_PAYLOAD",
        `${opts.snapshotUrl} does not match the Portfolio shape (schemaVersion !== 1 or cases not an array).`,
      );
    }

    return parsed;
  }

  return {
    listCases(): Promise<Case[]> {
      if (cache !== null) {
        // Return a fresh array so callers can safely sort/filter without
        // touching the cached snapshot.
        return Promise.resolve([...cache.cases]);
      }
      if (inFlight !== null) {
        return inFlight;
      }
      const pending = (async (): Promise<Case[]> => {
        try {
          const portfolio = await loadPortfolio();
          cache = portfolio;
          return [...portfolio.cases];
        } finally {
          // Clear only if we're still the current in-flight load. The
          // simple assignment below is safe because JS is single-threaded.
          inFlight = null;
        }
      })();
      inFlight = pending;
      return pending;
    },

    hasDeck(id: string): boolean {
      if (cache === null) return false;
      return cache.cases.some((c) => c.id === id);
    },

    fetchDeckUrl(id: string): string {
      if (cache === null) return "";
      if (!cache.cases.some((c) => c.id === id)) return "";
      return `${decksBase}/${id}.pptx`;
    },
  };
}
